import { Component, HostListener, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ReferralContextService } from './referral-context.service';
import { SheetLoggerService } from '../shared/services/sheet-logger.service';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { Loader } from '../shared/loader/loader';
import { Calendar } from '../calendar/calendar';

@Component({
  selector: 'app-referral-form',
  standalone: true,
  imports: [CommonModule, FormsModule, Navbar, Footer, Calendar, Loader],
  templateUrl: './referral-form.html',
  styleUrl: './referral.css'
})
export class ReferralForm implements OnInit {
  ipAddress = '';

  form = {
    firstname: '', middlename: '', lastname: '',
    dob: '', birthsex: '', email: '', phone: '',
    address: '', city: '', state: '', zip: '',
    submitterFirstName: '', submitterLastName: '', submitterPhone: '',
    Submitteremail: '', Submitterfax: '',
    affiliatedCompany: '', reasonForReferral: '', smsConsent: false,
  };

  loading = false;
  otherCompany = false;
  submitted = false;

  show65PlusModal: boolean = false;
  show65PlusUncertainModal: boolean = false;
  screening65Plus = {
    cognitiveDiagnosis: null as boolean | null,
    medicationDependence: null as boolean | null,
    poaProxy: null as boolean | null,
    technologyAssist: null as boolean | null
  };
  private pendingProceedAfter65Plus: (() => void) | null = null;
  private bypass65PlusGateOnce: boolean = false;

  stateOptions = [
    'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'
  ];
  companies = [
    'Concert Health',
    'Duke Health',
    'Focused Addiction Recovery (FAR)',
    'April Health',
    'NOCD',
    'Three Oaks Behavioral Health & Wellness',
    'UNC Health',
    'Mayrx'
  ];

  // State quick-select cycling
  private stateCycleLetter: string = '';
  private stateCycleMatches: string[] = [];
  private stateCycleIndex: number = -1;

  constructor(private ctx: ReferralContextService, private router: Router, private sheet: SheetLoggerService, private cdr: ChangeDetectorRef) {
    try { this.fetchIp(); } catch {}
  }

  private pushDataLayerEvent(eventName: string, payload: Record<string, unknown> = {}): void {
    const normalizedEventName = String(eventName || '').trim();
    if (!normalizedEventName) return;
    try {
      const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
      dataLayer.push({
        event: normalizedEventName,
        ...payload
      });
    } catch {}
  }

  ngOnInit(): void {
    this.pushDataLayerEvent('ols_referral_page_viewed');
  }

  trackReferralOlderAdultsFormShown(): void {
    this.pushDataLayerEvent('ols_referral_older_adults_form_shown');
  }

  trackReferralOlderAdultsSeeAvailableSlotsClick(): void {
    this.pushDataLayerEvent('ols_referral_older_adults_see_available_slots_click');
  }

  // Global keyboard shortcuts for quick selection
  @HostListener('window:keydown', ['$event'])
  onGlobalKeydown(event: KeyboardEvent) {
    const target = event.target as HTMLElement;
    const tag = (target?.tagName || '').toLowerCase();
    const isTyping = tag === 'input' || tag === 'textarea' || tag === 'select';
    if (event.altKey || event.ctrlKey || event.metaKey) return;

    const key = (event.key || '').toLowerCase();

    // Birth Sex: f → female, m → male, p → Prefer not to say
    // Apply when the Birth Sex select has focus OR dropdown is closed but the field is active
    const isBirthSexFocused = (target?.id === 'birthSexSelect');
    if (!isTyping || isBirthSexFocused) {
      if (key === 'f') { this.form.birthsex = 'female'; return; }
      if (key === 'm') { this.form.birthsex = 'male'; return; }
      if (key === 'p') { this.form.birthsex = 'Prefer not to say'; return; }
    }

    // State cycling by letter
    const isStateFocused = (target?.id === 'stateSelect');
    if ((this.stateCycleMatches.length > 0 || isStateFocused || !isTyping) && key.length === 1 && key >= 'a' && key <= 'z') {
      if (this.stateCycleLetter !== key) {
        this.stateCycleLetter = key;
        this.stateCycleMatches = this.stateOptions.filter(s => (s || '').toLowerCase().startsWith(key));
        this.stateCycleIndex = -1;
      }
      if (this.stateCycleMatches.length > 0) {
        this.stateCycleIndex = (this.stateCycleIndex + 1) % this.stateCycleMatches.length;
        this.form.state = this.stateCycleMatches[this.stateCycleIndex];
      }
    }
  }

  private async fetchIp() {
    try { const res = await fetch('https://api.ipify.org?format=json'); const j = await res.json(); this.ipAddress = j?.ip || ''; } catch {}
  }

  private nowDate(): string { const d = new Date(); const mm = String(d.getMonth()+1).padStart(2,'0'); const dd = String(d.getDate()).padStart(2,'0'); const yyyy = d.getFullYear(); return `${mm}/${dd}/${yyyy}`; }
  private nowTime(): string { const d = new Date(); const hh = String(d.getHours()).padStart(2,'0'); const mi = String(d.getMinutes()).padStart(2,'0'); const ss = String(d.getSeconds()).padStart(2,'0'); return `${hh}:${mi}:${ss}`; }

  formatPhone(e: any) {
    const input = e.target as HTMLInputElement;
    let raw = input.value || '';
    const prev = this.form.phone || '';
    // Handle backspace over hyphens: if deleting and previous ended with '-' and new equals prev without last char, remove another char
    if (raw.length < prev.length && prev.endsWith('-') && raw === prev.slice(0, prev.length - 1)) {
      raw = prev.slice(0, prev.length - 2);
    }
    let digits = raw.replace(/\D/g, '').slice(0, 10);
    let formatted = '';
    if (digits.length > 6) formatted = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
    else formatted = digits;
    this.form.phone = formatted;
    input.value = formatted;
  }

  formatSubmitterPhone(e: any) {
    const input = e.target as HTMLInputElement;
    let raw = input.value || '';
    const prev = this.form.submitterPhone || '';
    if (raw.length < prev.length && prev.endsWith('-') && raw === prev.slice(0, prev.length - 1)) {
      raw = prev.slice(0, prev.length - 2);
    }
    let digits = raw.replace(/\D/g, '').slice(0, 10);
    let formatted = '';
    if (digits.length > 6) formatted = `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
    else if (digits.length > 3) formatted = `${digits.slice(0,3)}-${digits.slice(3)}`;
    else formatted = digits;
    this.form.submitterPhone = formatted;
    input.value = formatted;
  }

  get screening65PlusCanProceed(): boolean {
    if (this.screening65Plus.cognitiveDiagnosis !== false) return false;
    if (this.screening65Plus.medicationDependence !== false) return false;
    if (this.screening65Plus.poaProxy !== false) return false;
    return this.screening65Plus.technologyAssist !== null;
  }

  close65PlusModal(): void {
    this.show65PlusModal = false;
    this.pendingProceedAfter65Plus = null;
    this.reset65PlusScreeningAnswers();
  }

  on65PlusProceed(): void {
    if (!this.screening65PlusCanProceed) return;
    this.show65PlusModal = false;
    if (this.screening65Plus.technologyAssist === true) {
      this.show65PlusUncertainModal = true;
      return;
    }
    const next = this.pendingProceedAfter65Plus;
    this.pendingProceedAfter65Plus = null;
    this.reset65PlusScreeningAnswers();
    if (next) next();
  }

  on65PlusUncertainClose(): void {
    this.show65PlusUncertainModal = false;
    const next = this.pendingProceedAfter65Plus;
    this.pendingProceedAfter65Plus = null;
    this.reset65PlusScreeningAnswers();
    if (next) next();
  }

  private shouldShow65PlusScreening(): boolean {
    return false;
  }

  private reset65PlusScreeningAnswers(): void {
    this.screening65Plus = {
      cognitiveDiagnosis: null,
      medicationDependence: null,
      poaProxy: null,
      technologyAssist: null
    };
  }

  private computeAgeForScope(rawDob: string): number | null {
    try {
      const text = (rawDob || '').toString().trim();
      if (!text) return null;
      let dob: Date | null = null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
        const [y, m, d] = text.split('-').map(n => parseInt(n, 10));
        dob = new Date(y, m - 1, d);
      } else {
        const normalized = text.replace(/\./g, '/').replace(/-/g, '/');
        const parts = normalized.split('/');
        if (parts.length === 3) {
          const mm = parseInt(parts[0], 10);
          const dd = parseInt(parts[1], 10);
          const yyyy = parseInt(parts[2], 10);
          dob = new Date(yyyy, mm - 1, dd);
        }
      }
      if (!dob || !isFinite(dob.getTime())) return null;
      const now = new Date();
      let age = now.getFullYear() - dob.getFullYear();
      const monthDiff = now.getMonth() - dob.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) age--;
      return Number.isFinite(age) ? age : null;
    } catch {
      return null;
    }
  }

  async onNext(trackEvent: boolean = true) {
    if (trackEvent) {
      this.pushDataLayerEvent('ols_referral_next_button_click');
    }
    this.submitted = true;
    if (!this.form.firstname.trim() || !this.form.lastname.trim() || !this.form.dob || !this.form.birthsex || !this.form.email || !this.form.phone || !this.form.state || !this.form.smsConsent) return;

    const bypass65GateForThisSubmit = this.bypass65PlusGateOnce;
    this.bypass65PlusGateOnce = false;
    if (this.shouldShow65PlusScreening() && !bypass65GateForThisSubmit) {
      this.pendingProceedAfter65Plus = () => {
        this.bypass65PlusGateOnce = true;
        this.onNext(false);
      };
      this.show65PlusModal = true;
      this.trackReferralOlderAdultsFormShown();
      try { this.cdr.detectChanges(); } catch {}
      return;
    }

    this.loading = true;
    try {
      const currentDate = this.nowDate();
      const currentTime = this.nowTime();
      this.ctx.setSubmissionDateTime(currentDate, currentTime);
      // Normalize affiliated company if 'Other' was selected without text
      if (this.otherCompany && !this.form.affiliatedCompany) {
        this.form.affiliatedCompany = 'Other';
      }
      // Build FormData to mirror React submission to Apps Script
      const formDataPat = new FormData();
      formDataPat.append('Source', 'Online Intake');
      formDataPat.append('Date_received', currentDate);
      formDataPat.append('Time_received', currentTime);
      formDataPat.append('First_name', this.form.firstname.trim());
      formDataPat.append('Middle_name', this.form.middlename.trim());
      formDataPat.append('Last_name', this.form.lastname.trim());
      formDataPat.append('DOB', this.form.dob.replace(/-/g,'/'));
      const sex = this.form.birthsex === 'female' ? 'F' : (this.form.birthsex === 'male' ? 'M' : 'U');
      formDataPat.append('Birthsex', sex);
      formDataPat.append('Phone', this.form.phone);
      formDataPat.append('Email', this.form.email);
      formDataPat.append('Scheduled', 'No');
      formDataPat.append('Appointment_date', '');
      formDataPat.append('IP_address', this.ipAddress);
      formDataPat.append('State', this.form.state);
      formDataPat.append('Reason_for_referral', this.form.reasonForReferral || '');
      formDataPat.append('Source_type', 'Referral');
      await fetch('https://script.google.com/macros/s/AKfycbzG28eLUEXRigGK7LLv0Z5CSXYHceK7_-kIJYsyCX1ByYGTLjXjDWb5lPfgW1CRJHC3lQ/exec', { method: 'POST', body: formDataPat });
      // cache for next step
      this.ctx.setFormData(this.form);
      this.router.navigate(['/referral/state']);
    } catch {
    } finally {
      this.loading = false;
    }
  }

  onCompanyChange(val: string) {
    if (val === 'Other') {
      this.otherCompany = true;
      this.form.affiliatedCompany = '';
    } else {
      this.otherCompany = false;
      this.form.affiliatedCompany = val || '';
    }
  }
}


